import { useCallback, useState } from "react"

import styles from "../ConfirmDialog.module.scss"

import ModalDialog from "@/main/components/ui/ModalDialog/ModalDialog"
import { useSouhlAdminMutation } from "@/provider/apollo/Apollo"
import { graphql } from "@/souhl_admin/gql/__generated__"

// --- GraphQL ---

const EnqueueSendAccountingEmailsBySettlementIDs = graphql(/* GraphQL */ `
	mutation EnqueueSendAccountingEmailsBySettlementIDs(
		$input: EnqueueSendAccountingEmailsBySettlementIDsInput!
	) {
		enqueueSendAccountingEmailsBySettlementIDs(input: $input) {
			enqueuedSettlementIds
		}
	}
`)

type Props = {
	onSendComplete: () => void
}

export const useSendEmailDialog = ({ onSendComplete }: Props) => {
	const [isOpen, setIsOpen] = useState(false)
	const [targetSettlement, setTargetSettlement] = useState<{
		id: string
		yearMonth: { year: number; month: number }
	} | null>(null)
	const [error, setError] = useState<string | null>(null)

	const [enqueueSendEmail, { loading }] = useSouhlAdminMutation(
		EnqueueSendAccountingEmailsBySettlementIDs,
		{
			context: { disableErrorDialog: true },
		}
	)

	const openDialog = useCallback(
		(settlementId: string, yearMonth: { year: number; month: number }) => {
			setTargetSettlement({ id: settlementId, yearMonth })
			setError(null)
			setIsOpen(true)
		},
		[]
	)

	const handleSend = useCallback(() => {
		if (!targetSettlement) return
		setError(null)

		enqueueSendEmail({
			variables: {
				input: {
					settlementIds: [targetSettlement.id],
				},
			},
		})
			.then(() => {
				setIsOpen(false)
				onSendComplete()
			})
			.catch((e: unknown) => {
				let msg = "メールの送信に失敗しました。"
				if (e instanceof Error) {
					msg = e.message
				}
				setError(msg)
				console.error("sendEmail error:", e)
			})
	}, [targetSettlement, enqueueSendEmail, onSendComplete])

	const render = useCallback(() => {
		return (
			<ModalDialog isOpen={isOpen} onOpenChange={setIsOpen}>
				<div className={styles.dialog_content}>
					<h2 className={styles.title}>メールを送信する</h2>
					{error && <p className={styles.error}>{error}</p>}
					<p className={styles.message}>
						{targetSettlement
							? `${targetSettlement.yearMonth.year}年${targetSettlement.yearMonth.month}月の相殺明細のメールを送信します。よろしいですか？`
							: ""}
					</p>
					<div className={styles.button_container}>
						<button
							type="button"
							className={styles.cancel_button}
							onClick={() => setIsOpen(false)}
							disabled={loading}
						>
							キャンセル
						</button>
						<button
							type="button"
							className={styles.confirm_button}
							onClick={handleSend}
							disabled={loading}
						>
							{loading ? "処理中..." : "送信する"}
						</button>
					</div>
				</div>
			</ModalDialog>
		)
	}, [isOpen, targetSettlement, error, loading, handleSend])

	return {
		render,
		openDialog,
	}
}
